// ==============================================
// BotAdminDash.jsx
// Admin dashboard for managing AI models from Firestore document platformsettings/aimodels
// ==============================================

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { authManager } from '@/managers/AuthManager'; // Assuming authManager can provide the current user
import { db } from '@/firebase/config';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { 
    Plus, 
    Edit2, 
    Trash2, 
    Search,
    ChevronDown,
    ChevronRight,
    Loader2,
    Settings2, // For AI Models icon
    AlertTriangle
} from 'lucide-react';

import ModelForm from './ModelForm'; // The form we just created

const ADMIN_USER_ID = 'UYWnAh2c48bmarKq6Dpvcr1UWqk2';
const AI_MODELS_DOC_PATH = 'platformsettings/aimodels';

const BotAdminDash = () => {
    const [aiModelsDoc, setAiModelsDoc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedPlatforms, setExpandedPlatforms] = useState({});

    // State for managing the ModelForm modal
    const [showModelForm, setShowModelForm] = useState(false);
    const [editingModelData, setEditingModelData] = useState(null); // { platformName, modelId, modelDetails }
    const [isNewModel, setIsNewModel] = useState(false);

    const fetchAiModels = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const currentUser = authManager.getCurrentUser(); // Make sure authManager is initialized and provides user
            if (!currentUser || currentUser.uid !== ADMIN_USER_ID) {
                throw new Error('Admin access required. UID mismatch.');
            }

            const docRef = doc(db, AI_MODELS_DOC_PATH);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                setAiModelsDoc(data);
                const initialExpanded = {};
                if (data.models) {
                    Object.keys(data.models).forEach(platform => {
                        initialExpanded[platform] = true; 
                    });
                }
                setExpandedPlatforms(initialExpanded);
            } else {
                setError(`Document ${AI_MODELS_DOC_PATH} does not exist. You can create it by adding the first model.`);
                setAiModelsDoc({ lastUpdated: null, models: {} }); 
            }
        } catch (err) {
            console.error("Error fetching AI models:", err);
            setError(`Failed to fetch AI models: ${err.message}`);
            toast.error(`Failed to fetch AI models: ${err.message}`);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAiModels();
    }, [fetchAiModels]);

    const handleTogglePlatform = (platformName) => {
        setExpandedPlatforms(prev => ({
            ...prev,
            [platformName]: !prev[platformName]
        }));
    };

    const handleOpenEditModelForm = (platformName, modelId, modelDetails) => {
        setEditingModelData({ platformName, modelId, modelDetails });
        setIsNewModel(false);
        setShowModelForm(true);
    };
    
    const handleOpenAddModelForm = (platformName = null) => {
        // For a completely new model, platformName might be null initially,
        // ModelForm will handle selecting or creating a new platform.
        // If platformName is provided, it's for adding to an existing platform.
        setEditingModelData({ 
            platformName: platformName, 
            modelId: '', // Model ID will be set in the form
            modelDetails: {} // ModelForm uses INITIAL_MODEL_DETAILS
        });
        setIsNewModel(true);
        setShowModelForm(true);
    };

    const handleDeleteModel = async (platformName, modelId) => {
        if (!window.confirm(`Are you sure you want to delete model "${modelId}" from platform "${platformName}"? This is irreversible.`)) return;
        
        setIsSaving(true);
        try {
            const currentModelsData = aiModelsDoc?.models || {};
            if (!currentModelsData[platformName] || !currentModelsData[platformName][modelId]) {
                throw new Error("Model or platform not found for deletion.");
            }

            const updatedPlatformModels = { ...currentModelsData[platformName] };
            delete updatedPlatformModels[modelId];

            const updatedModelsRoot = { ...currentModelsData };
            if (Object.keys(updatedPlatformModels).length === 0) {
                delete updatedModelsRoot[platformName];
            } else {
                updatedModelsRoot[platformName] = updatedPlatformModels;
            }
            
            const docRef = doc(db, AI_MODELS_DOC_PATH);
            await setDoc(docRef, {
                models: updatedModelsRoot,
                lastUpdated: Timestamp.now()
            }, { merge: true }); // merge:true will create the doc if it doesn't exist, or update specific fields

            toast.success(`Model ${modelId} deleted successfully from ${platformName}.`);
            fetchAiModels(); // Refresh data
        } catch (error) {
            console.error("Error deleting model:", error);
            toast.error(`Failed to delete model: ${error.message}`);
        } finally {
            setIsSaving(false);
        }
    };
    
    const handleFormSubmit = async (formDataFromModal) => {
        // formDataFromModal will be { platformName, modelId, modelDetails }
        setIsSaving(true);
        setShowModelForm(false);

        try {
            const { platformName, modelId, modelDetails } = formDataFromModal;
            
            const docRef = doc(db, AI_MODELS_DOC_PATH);
            const currentDoc = await getDoc(docRef); // Get current doc to avoid race conditions if possible, or rely on setDoc's behavior
            
            let updatedModels = currentDoc.exists() ? (currentDoc.data().models || {}) : {};

            if (!updatedModels[platformName]) {
                updatedModels[platformName] = {};
            }
            updatedModels[platformName][modelId] = modelDetails;

            await setDoc(docRef, {
                models: updatedModels,
                lastUpdated: Timestamp.now()
            }, { merge: true }); // Using merge:true is crucial here

            toast.success(`Model ${modelDetails.cleanModelName} (${modelId}) ${isNewModel ? 'added' : 'updated'} successfully!`);
            fetchAiModels(); // Refresh data
        } catch (error) {
            console.error("Error saving model:", error);
            toast.error(`Failed to save model: ${error.message}`);
        } finally {
            setIsSaving(false);
            setEditingModelData(null); // Clear editing state
        }
    };

    const getFilteredModels = () => {
        if (!aiModelsDoc || !aiModelsDoc.models) return {};
        if (!searchQuery) return aiModelsDoc.models;

        const filtered = {};
        const query = searchQuery.toLowerCase();

        for (const platform in aiModelsDoc.models) {
            const platformModels = aiModelsDoc.models[platform];
            const matchingModels = {};
            let platformMatches = platform.toLowerCase().includes(query);

            for (const modelId in platformModels) {
                const model = platformModels[modelId];
                if (
                    modelId.toLowerCase().includes(query) ||
                    model.cleanModelName?.toLowerCase().includes(query)
                ) {
                    matchingModels[modelId] = model;
                    platformMatches = true; 
                }
            }
            if (platformMatches && Object.keys(matchingModels).length > 0) {
                 filtered[platform] = matchingModels;
            } else if (platform.toLowerCase().includes(query) && Object.keys(matchingModels).length === 0) { // if platform name matches, show all its models
                filtered[platform] = platformModels; 
            }
        }
        return filtered;
    };
    
    const displayedModelsByPlatform = getFilteredModels();

    if (isLoading && !aiModelsDoc) {
        return (
            <div className="flex items-center justify-center h-screen bg-surface-dark">
                <Loader2 className="w-12 h-12 animate-spin text-primary" />
                <p className="ml-4 text-xl">Loading AI Models...</p>
            </div>
        );
    }

    if (error && !aiModelsDoc?.models) { 
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-surface-dark p-4">
                <AlertTriangle className="w-16 h-16 text-error mb-4" />
                <h2 className="text-2xl font-semibold mb-2">Error Loading Configuration</h2>
                <p className="text-on-surface-dim mb-4 text-center">{error}</p>
                 <button
                    onClick={() => handleOpenAddModelForm()}
                    className="mt-4 mb-2 inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Attempt to Create Document by Adding First Model
                </button>
                <button
                    onClick={fetchAiModels}
                    className="px-4 py-2 border border-border-color text-on-surface rounded-lg hover:bg-surface"
                >
                    Retry Fetch
                </button>
            </div>
        );
    }
    
    return (
        <div className="min-h-screen bg-gradient-to-b from-surface to-surface-dark text-on-surface p-4 sm:p-8">
            <div className="max-w-5xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center space-x-3">
                        <Settings2 className="w-10 h-10 text-primary" />
                        <h1 className="text-3xl font-bold">AI Models Configuration</h1>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="relative">
                            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-on-surface-dim" />
                            <input
                                type="text"
                                placeholder="Search platforms or models..."
                                className="pl-10 pr-4 py-2 w-full sm:w-72 bg-surface border border-border-color rounded-full focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => handleOpenAddModelForm()}
                            className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-full hover:bg-primary-dark transition-all duration-200 shadow-lg hover:shadow-xl"
                            title="Add New Model"
                            disabled={isSaving}
                        >
                            {isSaving ? <Loader2 className="w-5 h-5 animate-spin sm:mr-2" /> : <Plus className="w-5 h-5 sm:mr-2" />}
                            <span className="hidden sm:inline">New Model</span>
                        </button>
                    </div>
                </div>

                {(isLoading || isSaving) && <Loader2 className="w-6 h-6 animate-spin text-primary my-4 mx-auto" />}
                {error && !aiModelsDoc?.models && <p className="text-error bg-error/10 p-3 rounded-md my-4">Error: {error}</p>}


                {aiModelsDoc?.lastUpdated && (
                    <p className="text-sm text-on-surface-dim mb-6">
                        Last updated: {new Date(aiModelsDoc.lastUpdated.toDate ? aiModelsDoc.lastUpdated.toDate() : aiModelsDoc.lastUpdated).toLocaleString()}
                    </p>
                )}

                {Object.keys(displayedModelsByPlatform).length === 0 && !isLoading ? (
                    <div className="p-8 text-center bg-surface rounded-2xl shadow-xl border border-border-color">
                        <Settings2 className="w-16 h-16 text-on-surface-dim mx-auto mb-4" />
                        <p className="text-xl text-on-surface-dim mb-3">
                            {searchQuery ? "No matching platforms or models found." : "No AI models configured yet."}
                        </p>
                        <p className="text-sm text-on-surface-dim mb-4">
                            {searchQuery ? "Try a different search term." : "Add your first AI model to get started."}
                        </p>
                        {!searchQuery && (
                             <button
                                onClick={() => handleOpenAddModelForm()}
                                className="mt-4 inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                                disabled={isSaving}
                            >
                                <Plus className="w-5 h-5 mr-2" />
                                Add First Model
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-6">
                        {Object.entries(displayedModelsByPlatform).map(([platformName, models]) => (
                            <div key={platformName} className="bg-surface rounded-2xl shadow-xl border border-border-color overflow-hidden">
                                <button
                                    onClick={() => handleTogglePlatform(platformName)}
                                    className="w-full flex items-center justify-between p-4 sm:p-6 bg-surface-dark hover:bg-surface-dark/80 transition-colors"
                                >
                                    <h2 className="text-xl sm:text-2xl font-semibold text-primary">{platformName} ({Object.keys(models).length})</h2>
                                    {expandedPlatforms[platformName] ? <ChevronDown className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
                                </button>
                                {expandedPlatforms[platformName] && (
                                    <div className="p-4 sm:p-6 space-y-3">
                                        {Object.keys(models).length === 0 ? (
                                            <p className="text-on-surface-dim py-4 text-center">No models found for this platform{searchQuery ? " matching your search" : ""}.</p>
                                        ) : (
                                            Object.entries(models).sort(([,a],[,b]) => (a.order || 999) - (b.order || 999)).map(([modelId, details]) => (
                                            <div key={modelId} className="p-3 sm:p-4 bg-surface-dark/50 rounded-lg border border-border-color/50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                                <div>
                                                    <h3 className="font-semibold text-base sm:text-lg">{details.cleanModelName}</h3>
                                                    <p className="text-xs sm:text-sm text-on-surface-dim">ID: {modelId}</p>
                                                    <p className="text-xs sm:text-sm text-on-surface-dim">Order: {details.order ?? 'N/A'}, Max Tokens: {details.maxTokens ?? 'N/A'}</p>
                                                    <p className="text-xs sm:text-sm text-on-surface-dim">Category: {details.category ?? 'N/A'}</p>
                                                    <p className="text-xs sm:text-sm text-on-surface-dim">
                                                        Pricing (In/Out per 1M): 
                                                        ${details.tokenPricing?.[0]?.toFixed(4) ?? 'N/A'} / ${details.tokenPricing?.[1]?.toFixed(4) ?? 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="flex space-x-2 mt-2 sm:mt-0 self-end sm:self-center">
                                                    <button
                                                        onClick={() => handleOpenEditModelForm(platformName, modelId, details)}
                                                        className="p-2 text-on-surface-dim hover:text-primary transition-colors"
                                                        title="Edit Model"
                                                        disabled={isSaving}
                                                    >
                                                        <Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteModel(platformName, modelId)}
                                                        className="p-2 text-on-surface-dim hover:text-error transition-colors"
                                                        title="Delete Model"
                                                        disabled={isSaving}
                                                    >
                                                        <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        )))}
                                        <button
                                            onClick={() => handleOpenAddModelForm(platformName)}
                                            className="mt-3 inline-flex items-center px-3 py-1.5 text-sm bg-primary/20 text-primary rounded-md hover:bg-primary/30 transition-colors"
                                            disabled={isSaving}
                                        >
                                            <Plus className="w-4 h-4 mr-2" />
                                            Add Model to {platformName}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {showModelForm && (
                <ModelForm
                    modelData={editingModelData}
                    isNew={isNewModel}
                    onSubmit={handleFormSubmit}
                    onCancel={() => {
                        setShowModelForm(false);
                        setEditingModelData(null); // Clear editing state on cancel
                    }}
                    allModelsDoc={aiModelsDoc} 
                />
            )}
        </div>
    );
};

export default BotAdminDash;